import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@astro/auth'
import { FaRegUser } from "react-icons/fa6";
import CallDropdown from './CallDropdown'
import CalendarDropdown from './CalendarDropdown'
import AccountDropdown from './AccountDropdown'
import MobileMenu from './MobileMenu'
import BrandLogo from './BrandLogo'
import { useToast } from '../../context/ToastContext'
import { appUrl } from '@astro/config';
import { MdArrowOutward, MdOutlineArrowOutward } from "react-icons/md";
import { GrPhone } from "react-icons/gr";

const QUICK_LINKS = [
  { label: 'MAIN SITE', href: 'website', active: true },
  { label: 'JYOTISH', href: 'jyotish', active: false },
  { label: 'ACADEMY', href: 'education', active: false },
  { label: 'DIVINE SHOP', href: 'shop', active: false },
]

const NAV_LINKS = [
  { label: 'Home', to: '/', internal: true },
  { label: 'About', to: '/about', internal: true },
  { label: 'Consultation', to: 'jyotish', internal: false },
  { label: 'Academy', to: 'education', internal: false },
  { label: 'Astro Tools', to: '/astro-tools', internal: true },
  { label: 'Horoscope', to: '/horoscope', internal: true },
  { label: 'Shop', to: 'shop', internal: false },
  { label: 'Blogs', to: '/blog', internal: true },
]

export default function Header() {
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [backdropShown, setBackdropShown] = useState(false)
  const [backdropFaded, setBackdropFaded] = useState(true)
  const closeTimeoutRef = useRef(null)
  const { showNotification } = useToast()
  const { status, user, login } = useAuth()
  const authed = status === 'authenticated'
  const name = authed && user ? user.name || user.email : 'Account'
  // Chip label: the handle only — a truncated raw email ("DEBA@GMAIL…") reads badly.
  const displayName = name.split('@')[0]
  const initial = (name.trim()[0] || 'A').toUpperCase()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!openDropdown) return undefined
    const closeAll = () => setOpenDropdown(null)
    document.addEventListener('click', closeAll)
    return () => document.removeEventListener('click', closeAll)
  }, [openDropdown])

  const toggleDropdown = (name) => (e) => {
    e.stopPropagation()
    setOpenDropdown((prev) => (prev === name ? null : name))
  }

  const openMenu = () => {
    window.clearTimeout(closeTimeoutRef.current)
    setDrawerOpen(true)
    setBackdropShown(true)
    window.setTimeout(() => setBackdropFaded(false), 50)
  }

  const closeMenu = () => {
    setDrawerOpen(false)
    setBackdropFaded(true)
    closeTimeoutRef.current = window.setTimeout(() => setBackdropShown(false), 300)
  }

  const handleAppt = () => {
    closeMenu()
    const el = document.getElementById('cta')
    if (el) window.scrollTo({ top: el.offsetTop - 120, behavior: 'smooth' })
    showNotification('Scheduler Loaded')
  }

  const handleVault = () => {
    closeMenu()
    // The /dashboard route is guarded by RequireAuth, which bounces
    // unauthenticated visitors to the central login with returnTo preserved.
    navigate('/dashboard')
  }

  const scrollToSection = (id) => {
    closeMenu()
    const doScroll = () => {
      const el = document.getElementById(id)
      if (el) window.scrollTo({ top: el.offsetTop - 120, behavior: 'smooth' })
    }
    if (window.location.pathname === '/') {
      doScroll()
    } else {
      navigate('/')
      window.setTimeout(doScroll, 300)
    }
  }

  return (
    <>
      <div id="sticky-header" className="fixed top-0 left-0 right-0 z-50">

        {/* Sticky top promo strip */}
        <div className="bg-linear-to-r from-[#1C120C] via-astroDark to-[#1C120C] border-b border-goldPrimary/10 py-2.5 px-4 sm:px-12 text-[8px] sm:text-[10px] tracking-[0.2em] font-semibold text-white/60 flex flex-col lg:flex-row justify-between items-center select-none gap-2.5 sm:gap-4">

          <div className="flex items-center gap-1 uppercase">
            <span className="text-goldPrimary animate-pulse">✦</span>ASTRO RAJU MAHARAJ JI{' '}
            <span className="text-goldPrimary animate-pulse">✦</span>
          </div>

          <nav className="hidden lg:flex items-center justify-center gap-4 sm:gap-6 uppercase overflow-x-auto max-w-full no-scrollbar pb-1 sm:pb-0">
            {QUICK_LINKS.map((link) => (
              <a
                key={link.label}
                href={appUrl(link.href)}
                className={`hover:text-goldPrimary  flex items-center ${link.active ? "bg-secondary/15 text-goldPrimary px-3 py-1 rounded" : ""}`}
              >
                {link.label}
                <span className={`ml-1 ${link.active ? "text-goldPrimary hidden" : ""}`}>
                  <MdArrowOutward size={14} />
                </span>
              </a>
            ))}
          </nav>
        </div>

        <div className="relative">
          {/* Transparent over the dark hero at the top; solid white with blur on scroll. */}
          <div className={`absolute inset-0 -z-10 border-b transition-all duration-300 ${scrolled ? 'backdrop-blur-md bg-white/95 border-black/10 shadow-sm' : 'bg-transparent border-transparent'}`}></div>
          <nav id="navbar" className="py-4 transition-all duration-300 whitespace-nowrap">
            <div className="max-w-7xl 2xl:max-w-400 mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 flex items-center justify-between relative">
              <a
                href={appUrl('website')}
                className="flex items-center gap-2 sm:gap-3"
              >
                <BrandLogo className={`h-10 w-auto sm:h-12 max-w-20 rounded-xl transition-all duration-300 ${scrolled ? '' : 'bg-[#FBF3E4] border border-goldPrimary/40 shadow-lg shadow-black/30'}`}>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-linear-to-tr from-goldAccent to-goldPrimary flex items-center justify-center text-astroDark font-bold text-lg sm:text-xl shadow-lg">
                    ॐ
                  </div>
                </BrandLogo>
                <div className="flex flex-col">
                  <span className={`font-royal text-sm sm:text-base font-bold tracking-widest leading-none transition-colors ${scrolled ? 'text-primary' : 'text-goldPrimary'}`}>
                    ASTRO RAJU MAHARAJ JI
                  </span>
                  <span className={`text-[8px] sm:text-[9px] font-semibold tracking-[0.25em] uppercase mt-1 transition-colors ${scrolled ? 'text-primary/50' : 'text-white/50'}`}>
                    Vedic Wisdom Master
                  </span>
                </div>
              </a>

              <div className="hidden xl:flex items-center gap-4 2xl:gap-8 text-[11px] uppercase font-medium tracking-widest">
                {NAV_LINKS.map((link, i) => (
                  link.scrollTo ? (
                    <button
                      key={link.label}
                      type="button"
                      onClick={() => scrollToSection(link.scrollTo)}
                      className={`transition-colors uppercase cursor-pointer ${scrolled ? 'text-primary hover:text-goldPrimary' : 'text-white/70 hover:text-goldPrimary'}`}
                    >
                      {link.label}
                    </button>
                  ) : link.internal ? (
                    <NavLink
                      key={link.label}
                      to={link.to}
                      className={(e) => `transition-colors ${ e.isActive
                        ? `text-goldPrimary ${scrolled ? 'hover:text-primary' : 'hover:text-white'}`
                        : scrolled
                          ? 'text-primary hover:text-goldPrimary'
                          : 'text-white/70 hover:text-goldPrimary'
                        }`}
                    >
                      {link.label}
                    </NavLink>
                  ) : (
                    <a
                      key={link.label}
                      href={appUrl(link.to)}
                      className={`transition-colors flex items-center ${scrolled
                          ? 'text-primary hover:text-goldPrimary'
                          : 'text-white/70 hover:text-goldPrimary'
                        }`}
                    >
                      {link.label} <MdOutlineArrowOutward />
                    </a>
                  )
                ))}
              </div>

              <div className="hidden sm:flex items-center gap-3 md:gap-4 xl:gap-2 2xl:gap-4 relative">
                <div className="relative">
                  <button
                    type="button"
                    onClick={toggleDropdown('call')}
                    className={`p-2.5 rounded-full border border-transparent transition-all flex items-center justify-center ${scrolled ? 'text-primary hover:bg-black/5' : 'text-white/80 hover:text-goldPrimary hover:bg-white/5 hover:border-white/10'}`}
                    aria-label="Support Hotline"
                  >
                    <GrPhone size={20} />
                  </button>
                  <CallDropdown open={openDropdown === 'call'} />
                </div>

                <div className="relative">
                  <button
                    type="button"
                    onClick={toggleDropdown('calendar')}
                    className={`p-2.5 rounded-full border border-transparent transition-all flex items-center justify-center ${scrolled ? 'text-primary hover:bg-black/5' : 'text-white/80 hover:text-goldPrimary hover:bg-white/5 hover:border-white/10'}`}
                    aria-label="Book Session"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                      />
                    </svg>
                  </button>
                  <CalendarDropdown open={openDropdown === 'calendar'} />
                </div>

                <div className="relative">
                  {authed ? (
                    <>
                      <button
                        type="button"
                        onClick={toggleDropdown('account')}
                        className={`flex items-center gap-2 px-3 py-2 rounded-full border border-transparent transition-all ${scrolled ? 'text-primary hover:bg-black/5' : 'text-white/80 hover:text-goldPrimary hover:bg-white/5 hover:border-white/10'}`}
                      >
                        <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden border border-primary/20 bg-primary text-white">
                          {user?.avatarUrl ? (
                            <img src={user.avatarUrl} alt={name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[11px] uppercase leading-none font-bold">{initial}</span>
                          )}
                        </div>
                        <span className="hidden 2xl:block text-[11px] font-normal uppercase tracking-wider max-w-[12ch] truncate">{displayName}</span>
                        <svg className={`w-3.5 h-3.5 transition-transform duration-300 ${openDropdown === 'account' ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                      </button>
                      <AccountDropdown open={openDropdown === 'account'} onNavigate={() => setOpenDropdown(null)} />
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => login()}
                      className={`p-2.5 rounded-full border border-transparent transition-all flex items-center justify-center ${scrolled ? 'text-primary hover:bg-black/5' : 'text-white/80 hover:text-goldPrimary hover:bg-white/5 hover:border-white/10'}`}
                      aria-label="Sign in"
                    >
                      <FaRegUser size={20} />
                    </button>
                  )}
                </div>

                <Link
                  to="/contact"
                  className="shine-btn bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-xs tracking-widest uppercase px-4 2xl:px-5 py-3 rounded-full shadow-lg shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5"
                >
                  Contact Us
                </Link>
              </div>

              {/* Mobile cluster. The action row above is hidden below sm, so on a
                  phone the account control rides next to the hamburger — otherwise
                  there is no signal at all of whether you are signed in. */}
              <div className="flex xl:hidden items-center gap-1">
                <div className="relative sm:hidden">
                  {authed ? (
                    <>
                      <button
                        type="button"
                        onClick={toggleDropdown('account')}
                        className={`flex items-center p-1 rounded-full border border-transparent transition-all ${scrolled ? 'text-primary hover:bg-black/5' : 'text-white/80 hover:bg-white/5 hover:border-white/10'}`}
                        aria-label="Account"
                      >
                        <div className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden border border-primary/20 bg-primary text-white">
                          {user?.avatarUrl ? (
                            <img src={user.avatarUrl} alt={name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[11px] uppercase leading-none font-bold">{initial}</span>
                          )}
                        </div>
                      </button>
                      <AccountDropdown open={openDropdown === 'account'} onNavigate={() => setOpenDropdown(null)} />
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => login()}
                      className={`p-2 rounded-full border border-transparent transition-all flex items-center justify-center ${scrolled ? 'text-primary hover:bg-black/5' : 'text-white/80 hover:bg-white/5 hover:border-white/10'}`}
                      aria-label="Sign in"
                    >
                      <FaRegUser size={18} />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={openMenu}
                  className={`flex focus:outline-none p-2 transition-colors ${scrolled ? 'text-primary hover:text-goldPrimary' : 'text-white/85 hover:text-goldPrimary'}`}
                  aria-label="Toggle Menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </nav>
        </div>
      </div>

      <MobileMenu
        drawerOpen={drawerOpen}
        backdropShown={backdropShown}
        backdropFaded={backdropFaded}
        onClose={closeMenu}
        onAppt={handleAppt}
        onVault={handleVault}
        onSection={scrollToSection}
      />
    </>
  )
}
